import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { EnhancedSeoHead } from "@appcaire/shared/seo";
import { FAQ } from "@appcaire/shared/seo";
import {
  generateArticleSchema,
  generateFAQSchema,
  generateBreadcrumbSchema,
  combineSchemas,
} from "@appcaire/shared/seo";
import {
  Shield,
  Scale,
  Heart,
  UserCheck,
  AlertCircle,
  Phone,
  ArrowRight,
} from "lucide-react";

/**
 * Static data hoisted outside component (React best practice: rendering-hoist-jsx)
 */
const RIGHTS = [
  {
    icon: UserCheck,
    title: "Rätt till bedömning",
    description:
      "Du har alltid rätt att få dina behov utredda av kommunen, oavsett ålder eller situation.",
  },
  {
    icon: Scale,
    title: "Rätt att överklaga",
    description:
      "Om du inte är nöjd med beslutet kan du överklaga till förvaltningsrätten.",
  },
  {
    icon: Heart,
    title: "Rätt till värdigt liv",
    description:
      "Hemtjänsten ska utformas så att du kan leva ett värdigt liv med självbestämmande.",
  },
  {
    icon: Shield,
    title: "Rätt att välja anordnare",
    description:
      "I Nacka har du rätt att fritt välja och byta hemtjänstutförare.",
  },
];

const SERVICES = [
  {
    category: "Personlig omvårdnad",
    items: [
      "Hjälp att komma upp på morgonen",
      "Dusch och personlig hygien",
      "På- och avklädning",
      "Toalettbesök",
      "Hjälp med måltider",
      "Medicinpåminnelse",
    ],
  },
  {
    category: "Hushållsservice",
    items: [
      "Städning",
      "Tvätt och klädvård",
      "Inköp av mat och förnödenheter",
      "Ärenden på bank och apotek",
      "Matlagning",
    ],
  },
  {
    category: "Socialt stöd",
    items: [
      "Promenader och utevistelse",
      "Ledsagning till vårdbesök",
      "Social samvaro och aktiviteter",
      "Avlösning för anhöriga",
    ],
  },
];

const RATTIGHETER_FAQ_ITEMS = [
  {
    question: "Hur ansöker jag om hemtjänst i Nacka?",
    answer:
      "Du ansöker genom att kontakta Nacka kommuns socialtjänst. Du kan göra detta via Nacka kommuns webbplats (för dig över 65 år eller funktionshindrade), ringa servicecenter på 08-718 80 00, eller besöka Nacka stadshus. En biståndshandläggare kommer att kontakta dig för att boka ett möte och utreda dina behov.",
  },
  {
    question: "Vem bestämmer hur mycket hjälp jag får?",
    answer:
      "En biståndshandläggare på kommunen utreder dina behov och fattar beslut. Beslutet baseras på socialtjänstlagen och ska utgå från dina individuella behov. Du har rätt att överklaga om du inte håller med.",
  },
  {
    question: "Kan jag välja vilken hemtjänstutförare jag vill ha?",
    answer:
      "Ja! I Nacka har du rätt att fritt välja vilken godkänd anordnare som ska hjälpa dig. Du kan byta anordnare när du vill – utan att behöva ange skäl. Detta kallas kundval och innebär att makten ligger hos dig.",
  },
  {
    question: "Hur byter jag hemtjänstutförare?",
    answer:
      "Om du redan har hemtjänst hos en anordnare kan du byta genom att kontakta din biståndshandläggare. Du behöver inte säga upp ditt nuvarande avtal – biståndshandläggaren hjälper dig med det. Det är enkelt att byta i Nacka tack vare kundvalssystemet.",
  },
  {
    question: "Vad kostar hemtjänst i Nacka?",
    answer:
      "Avgiften för hemtjänst i Nacka kommun är 312 kr per timme. Nacka kommuns högsta avgift per månad är 2 575 kr oavsett antal timmar. Det finns möjlighet att få reducerad avgift beroende på inkomst. Avgiften är densamma oavsett om du väljer en privat eller kommunal anordnare.",
  },
  {
    question: "Vad kan hemtjänsten hjälpa mig med?",
    answer:
      "Hemtjänsten kan hjälpa med personlig omvårdnad (dusch, påklädning, toalettbesök), städning, tvätt och klädvård, inköp av mat och förnödenheter, matlagning, promenader och ledsagning, socialt umgänge, avlösning för anhöriga och mycket mer. Vilken hjälp du får beror på ditt biståndsbeslut.",
  },
  {
    question: "Hur ofta kommer personalen?",
    answer:
      "Hur ofta personalen kommer beror på dina behov. Du kan få hemtjänst dygnet runt, alla dagar i veckan om du behöver det. Schemat anpassas efter ditt beslut och dina önskemål.",
  },
  {
    question: "Kan jag välja min egen personal?",
    answer:
      "Du kan önska att få viss personal, men du kan inte garantera att du alltid får den du önskar. Många anordnare strävar dock efter kontinuitet och försöker matcha dig med personal som passar dina behov.",
  },
  {
    question: "Vad händer om jag inte är nöjd med hemtjänsten?",
    answer:
      "Om du inte är nöjd kan du alltid kontakta din kontaktperson eller utförarens chef. Du kan också byta anordnare helt om du inte är nöjd. I Nacka är det enkelt att byta tack vare kundvalssystemet. Du kan också göra en anmälan till kommunen.",
  },
  {
    question: "Vad är skillnaden mellan hemtjänst och hemsjukvård?",
    answer:
      "Hemtjänst syftar till att ge dig hjälp med praktiska sysslor i vardagen, som städning, matlagning, personlig hygien och ledsagning. Hemtjänstpersonalen är inte utbildad att ge medicinsk vård. Hemsjukvård syftar till att ge medicinsk vård i hemmet, som läkemedelsbehandling, såromläggning, injektioner och rehabilitering. Hemsjukvårdspersonal är sjuksköterskor, undersköterskor eller arbetsterapeuter.",
  },
  {
    question: "Kan jag få rehabilitering genom hemtjänsten?",
    answer:
      "Ja, i viss utsträckning. Hemtjänsten kan erbjuda både vardagsrehabilitering (som ingår i ordinarie hemtjänst) och tidsbegränsad rehabilitering. Vardagsrehabilitering syftar till att du ska kunna klara av vardagens sysslor så självständigt som möjligt. Tidsbegränsad rehabilitering kan du få om du har drabbats av en sjukdom eller skada som kräver mer intensiv rehabilitering.",
  },
  {
    question: "Kan anhöriga närvara vid hemtjänstbesöken?",
    answer:
      "Ja, anhöriga är välkomna att vara med vid besöken om du önskar det. Många anordnare uppskattar samarbete med anhöriga och ser det som positivt för omsorgens kvalitet.",
  },
];

/**
 * Rattigheter - Rights and entitlements for home care recipients
 *
 * SEO Focus: "rättigheter hemtjänst", "vad ingår i hemtjänst"
 */
const Rattigheter: React.FC = () => {
  /**
   * Gets the correct absolute path for links (always root-level for standalone app)
   */
  const getPath = (path: string) => path;

  // Memoize schema generation (React best practice: rerender-memo)
  const articleSchema = useMemo(
    () =>
      generateArticleSchema({
        headline: "Dina rättigheter | Hemtjänst i Nacka",
        description:
          "Som hemtjänsttagare har du rättigheter som skyddas av lag. Här förklarar vi vad du kan förvänta dig och hur du gör om något inte fungerar.",
        url: "https://nackahemtjanst.se/rattigheter",
        datePublished: "2025-12-22",
        dateModified: "2026-01-09",
      }),
    [],
  );

  const faqSchema = useMemo(
    () =>
      generateFAQSchema(
        RATTIGHETER_FAQ_ITEMS.map((item) => ({
          question: item.question,
          answer: item.answer,
        })),
        "https://nackahemtjanst.se/rattigheter",
      ),
    [],
  );

  const breadcrumbSchema = useMemo(
    () =>
      generateBreadcrumbSchema([
        { name: "Hem", url: "https://nackahemtjanst.se/" },
        {
          name: "Rättigheter",
          url: "https://nackahemtjanst.se/rattigheter",
        },
      ]),
    [],
  );

  const combinedSchema = useMemo(
    () => combineSchemas([articleSchema, faqSchema, breadcrumbSchema]),
    [articleSchema, faqSchema, breadcrumbSchema],
  );
  return (
    <>
      <EnhancedSeoHead
        title="Dina rättigheter | Hemtjänst i Nacka"
        description="Som hemtjänsttagare har du rättigheter som skyddas av lag. Här förklarar vi vad du kan förvänta dig och hur du gör om något inte fungerar."
        canonicalUrl="https://nackahemtjanst.se/rattigheter"
        ogType="article"
        keywords={[
          "rättigheter hemtjänst",
          "hemtjänst rättigheter Nacka",
          "vad ingår i hemtjänst",
          "hemtjänst lag Nacka",
          "rättigheter hemtjänsttagare",
        ]}
        contentClassification="Consumer Guide"
        targetAudience="seniors, families"
        coverage="Sweden"
        publishDate="2025-12-22T00:00:00+01:00"
        modifiedDate="2026-01-09T00:00:00+01:00"
        author="Nacka Hemtjänst"
        schemaMarkup={combinedSchema ?? undefined}
        entities={[{ type: "Place", name: "Nacka" }]}
      />

      {/* Hero with Glass Effect */}
      <section className="relative bg-gradient-to-b from-purple-50 via-white to-white py-16 md:py-24 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-100/20 via-transparent to-emerald-100/20" />
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <div className="bg-glass-white backdrop-blur-glass border border-glass-border rounded-2xl p-6 inline-block mb-6 shadow-glass-light">
            <Shield className="w-12 h-12 text-purple-600" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-surface-900 tracking-tight mb-6">
            Dina rättigheter
          </h1>
          <p className="text-xl text-surface-600 max-w-2xl mx-auto leading-relaxed">
            Som hemtjänsttagare har du rättigheter som skyddas av lag. Här
            förklarar vi vad du kan förvänta dig och hur du gör om något inte
            fungerar.
          </p>
        </div>
      </section>

      {/* Core Rights with Glass Effect */}
      <section className="py-16 px-4 bg-gradient-to-b from-white to-surface-50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-surface-900 mb-12 text-center">
            Dina grundläggande rättigheter
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {RIGHTS.map((right, index) => (
              <div
                key={index}
                className="bg-glass-white backdrop-blur-glass border border-glass-border rounded-2xl p-6 shadow-glass-light hover:shadow-glass-hover transition-all"
              >
                <div className="flex items-start gap-4">
                  <div className="p-3 rounded-xl bg-purple-100">
                    <right.icon className="w-6 h-6 text-purple-700" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-purple-900 mb-2">
                      {right.title}
                    </h3>
                    <p className="text-purple-800">{right.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What's Included with Glass Effect */}
      <section className="py-16 px-4 bg-gradient-to-b from-surface-50 to-white">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-surface-900 mb-4 text-center">
            Vad kan hemtjänsten hjälpa till med?
          </h2>
          <p className="text-lg text-surface-600 text-center mb-12 max-w-2xl mx-auto">
            Vilken hjälp just du får beror på ditt beslut, men här är exempel på
            vad hemtjänsten kan omfatta.
          </p>

          <div className="grid md:grid-cols-3 gap-8">
            {SERVICES.map((service, index) => (
              <div
                key={index}
                className="bg-glass-white backdrop-blur-glass border border-glass-border rounded-2xl p-6 shadow-glass-light hover:shadow-glass-hover transition-all"
              >
                <h3 className="text-xl font-bold text-surface-900 mb-4">
                  {service.category}
                </h3>
                <ul className="space-y-2">
                  {service.items.map((item, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-2 text-surface-700"
                    >
                      <span className="text-emerald-600 mt-1">✓</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Appeal Process with Glass Effect */}
      <section className="py-16 px-4 bg-gradient-to-b from-white to-surface-50">
        <div className="max-w-4xl mx-auto">
          <div className="bg-glass-white backdrop-blur-glass border border-glass-border rounded-2xl p-8 shadow-glass-light">
            <div className="flex flex-col md:flex-row gap-8 items-start">
              <div className="flex-shrink-0">
                <AlertCircle className="w-12 h-12 text-amber-600" />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-amber-900 mb-4">
                  Inte nöjd med beslutet?
                </h2>
                <p className="text-lg text-amber-800 mb-6 leading-relaxed">
                  Om du tycker att beslutet om hemtjänst är felaktigt har du
                  rätt att överklaga. Så här gör du:
                </p>
                <ol className="space-y-4">
                  <li className="flex items-start gap-3">
                    <span className="w-8 h-8 rounded-full bg-amber-200 flex items-center justify-center flex-shrink-0 font-bold text-amber-800">
                      1
                    </span>
                    <div>
                      <strong className="text-amber-900">
                        Läs beslutet noggrant
                      </strong>
                      <p className="text-amber-800">
                        I beslutet står det hur du överklagar och hur lång tid
                        du har på dig.
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-8 h-8 rounded-full bg-amber-200 flex items-center justify-center flex-shrink-0 font-bold text-amber-800">
                      2
                    </span>
                    <div>
                      <strong className="text-amber-900">
                        Skriv ett överklagande
                      </strong>
                      <p className="text-amber-800">
                        Förklara varför du tycker beslutet är fel och vad du
                        vill istället.
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-8 h-8 rounded-full bg-amber-200 flex items-center justify-center flex-shrink-0 font-bold text-amber-800">
                      3
                    </span>
                    <div>
                      <strong className="text-amber-900">
                        Skicka till kommunen
                      </strong>
                      <p className="text-amber-800">
                        Kommunen skickar vidare till förvaltningsrätten som
                        prövar ditt ärende.
                      </p>
                    </div>
                  </li>
                </ol>
                <p className="mt-6 text-amber-800">
                  <strong>Tips:</strong> Du kan få gratis hjälp av kommunens
                  konsumentvägledare eller anhörigstöd om du behöver hjälp att
                  överklaga.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <FAQ
            items={RATTIGHETER_FAQ_ITEMS}
            title="Vanliga frågor om rättigheter"
          />
        </div>
      </section>

      {/* Contact CTA with Glass Effect */}
      <section className="py-16 px-4 bg-gradient-to-b from-white to-surface-50">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-glass-white backdrop-blur-glass border border-glass-border rounded-2xl p-8 shadow-glass-light">
            <Phone className="w-12 h-12 text-surface-400 mx-auto mb-6" />
            <h2 className="text-3xl font-bold text-surface-900 mb-6">
              Har du frågor om dina rättigheter?
            </h2>
            <p className="text-xl text-surface-600 mb-8 max-w-2xl mx-auto">
              Nacka kommuns servicecenter kan hjälpa dig att förstå dina
              rättigheter och svara på frågor om hemtjänst.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="tel:0871880000"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl bg-emerald-600 text-white text-lg font-semibold hover:bg-emerald-700 transition-all"
              >
                <Phone className="w-5 h-5" />
                Ring 08-718 80 00
              </a>
              <Link
                to={getPath("/kontakt")}
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl bg-glass-white backdrop-blur-glass border border-glass-border text-surface-700 text-lg font-semibold hover:bg-glass-hover transition-all shadow-glass-light"
              >
                Fler kontaktvägar
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Disclaimer with Glass Effect */}
      <section className="py-12 px-4 bg-gradient-to-b from-surface-50 to-white">
        <div className="max-w-4xl mx-auto">
          <div className="bg-glass-white backdrop-blur-glass border border-glass-border rounded-2xl p-6 shadow-glass-light">
            <p className="text-sm text-surface-600 leading-relaxed">
              <strong>Viktig information:</strong> Denna webbplats är en
              oberoende guide och är inte en del av Nacka kommun. För officiell
              information om dina rättigheter, kontakta alltid{" "}
              <a
                href="https://www.nacka.se"
                target="_blank"
                rel="noreferrer"
                className="text-emerald-600 font-medium hover:underline"
              >
                Nacka kommun
              </a>{" "}
              direkt eller läs på Socialstyrelsens webbplats.
            </p>
          </div>
        </div>
      </section>

      {/* Cross-link to hemtjanstguide.se */}
      <section className="py-12 px-4 bg-gradient-to-b from-white to-surface-50">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-glass-white backdrop-blur-glass border border-glass-border rounded-2xl p-6 shadow-glass-light">
            <p className="text-surface-600 mb-4">
              Letar du efter information om rättigheter i andra kommuner? På{" "}
              <a
                href="https://hemtjanstguide.se"
                target="_blank"
                rel="noreferrer"
                className="text-emerald-600 font-medium hover:underline"
              >
                hemtjanstguide.se
              </a>{" "}
              hittar du information om hemtjänst och rättigheter i alla Sveriges
              kommuner.
            </p>
          </div>
        </div>
      </section>
    </>
  );
};

export default Rattigheter;
